import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Database } from '@/integrations/supabase/types';

export type Club = Database['public']['Tables']['clubs']['Row'];

interface ClubContextType {
  // Existing selection-based functionality for user play flow
  selectedClubIndex: number;
  setSelectedClubIndex: (index: number) => void;
  handleClubSelection: (newIndex: number, clubName: string) => void;
  
  // New admin functionality for current club management
  currentClub: Club | null;
  setCurrentClub: (club: Club | null) => void;
  isClubAdmin: boolean;
  setIsClubAdmin: (isAdmin: boolean) => void;
}

const ClubContext = createContext<ClubContextType | undefined>(undefined);

export function useClubContext(): ClubContextType | undefined {
  const context = useContext(ClubContext);
  return context;
}

interface ClubProviderProps {
  children: React.ReactNode;
}

export function ClubProvider({ children }: ClubProviderProps) {
  const queryClient = useQueryClient();
  
  // Initialize from localStorage or default to 0
  const [selectedClubIndex, setSelectedClubIndex] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedIndex = localStorage.getItem('selectedClubIndex');
      if (savedIndex) {
        const parsedIndex = parseInt(savedIndex, 10);
        // Ensure we have a valid number, default to 0 if NaN
        if (isNaN(parsedIndex)) {
          console.warn('🚨 Invalid selectedClubIndex in localStorage:', savedIndex, 'defaulting to 0');
          // Don't set localStorage here - it causes setState during render warning
          return 0;
        }
        return parsedIndex;
      }
    }
    return 0;
  });

  // SECURITY FIX: Don't initialize from localStorage - requires server verification
  const [currentClub, setCurrentClub] = useState<Club | null>(null);

  // SECURITY FIX: Don't trust localStorage for admin status - requires server verification
  const [isClubAdmin, setIsClubAdmin] = useState(false);

  // Fix invalid localStorage value in an effect, not during render
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedIndex = localStorage.getItem('selectedClubIndex');
      if (savedIndex) {
        const parsedIndex = parseInt(savedIndex, 10);
        if (isNaN(parsedIndex)) {
          localStorage.setItem('selectedClubIndex', '0');
        }
      }
    }
  }, []);

  const handleClubSelection = useCallback((newIndex: number, clubName: string) => {
    console.log('🎯 handleClubSelection called:', { newIndex, clubName, currentIndex: selectedClubIndex });
    
    if (newIndex !== selectedClubIndex) {
      setSelectedClubIndex(newIndex);
      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('selectedClubIndex', newIndex.toString());
      }
      
      // Invalidate all club-related queries to refetch data for the new club
      queryClient.invalidateQueries({ queryKey: ['club-leaderboard'] });
      queryClient.invalidateQueries({ queryKey: ['club-stats'] });
      
      console.log('✅ Club selection updated - new index:', newIndex, 'queries invalidated');
    }
  }, [selectedClubIndex, queryClient]);

  // Update setSelectedClubIndex to also persist to localStorage
  const persistentSetSelectedClubIndex = useCallback((index: number) => {
    setSelectedClubIndex(index);
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedClubIndex', index.toString());
    }
    
    // Invalidate all club-related queries to refetch data for the new club
    queryClient.invalidateQueries({ queryKey: ['club-leaderboard'] });
    queryClient.invalidateQueries({ queryKey: ['club-stats'] });
  }, [queryClient]);

  // Persistent setters for admin functionality
  const persistentSetCurrentClub = useCallback((club: Club | null) => {
    setCurrentClub(club);
    if (typeof window !== 'undefined') {
      if (club) {
        localStorage.setItem('currentAdminClub', JSON.stringify(club));
      } else {
        localStorage.removeItem('currentAdminClub');
      }
    }
    
    // Invalidate club admin queries when switching clubs
    queryClient.invalidateQueries({ queryKey: ['admin-clubs'] });
    queryClient.invalidateQueries({ queryKey: ['club-details'] });
    queryClient.invalidateQueries({ queryKey: ['club-members'] });
    queryClient.invalidateQueries({ queryKey: ['club-bookings'] });
  }, [queryClient]);

  const persistentSetIsClubAdmin = useCallback((isAdmin: boolean) => {
    setIsClubAdmin(isAdmin);
    if (typeof window !== 'undefined') {
      localStorage.setItem('isClubAdmin', isAdmin.toString());
    }
  }, []);

  const value: ClubContextType = {
    selectedClubIndex,
    setSelectedClubIndex: persistentSetSelectedClubIndex,
    handleClubSelection,
    currentClub,
    setCurrentClub: persistentSetCurrentClub,
    isClubAdmin,
    setIsClubAdmin: persistentSetIsClubAdmin,
  };

  return (
    <ClubContext.Provider value={value}>
      {children}
    </ClubContext.Provider>
  );
}